// File: frontend/src/context/AuthContext.jsx

import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";

export const AuthContext = createContext();

const API_BASE_URL = "https://warfc.com/api";
const REFRESH_URL = `${API_BASE_URL}/auth/refresh`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("userInfo");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [token, setToken] = useState(() => {
    return localStorage.getItem("userToken") || null;
  });

  const [authLoading, setAuthLoading] = useState(true);

  // Prevent multiple API calls from refreshing the token simultaneously.
  const refreshPromiseRef = useRef(null);

  // ---------------------------------------------------------
  // UPDATE AUTH STATE
  // ---------------------------------------------------------

  const setAuth = useCallback((newToken, userObj) => {
    if (newToken) {
      localStorage.setItem("userToken", newToken);
      setToken(newToken);
    }

    if (userObj) {
      const normalizedUser = {
        ...userObj,
        id:
          userObj.id ??
          userObj.fighter_id ??
          userObj.user_id ??
          userObj.userId,
        gender: userObj.gender
          ? userObj.gender.toLowerCase()
          : userObj.gender,
      };

      localStorage.setItem(
        "userInfo",
        JSON.stringify(normalizedUser)
      );

      setUser(normalizedUser);
    }
  }, []);

  // ---------------------------------------------------------
  // REFRESH ACCESS TOKEN
  // ---------------------------------------------------------

  const refreshAccessToken = useCallback(async () => {
    /*
     * If another request is already refreshing the token,
     * wait for that same request instead of creating another
     * refresh request.
     */
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    refreshPromiseRef.current = (async () => {
      try {
        const response = await fetch(REFRESH_URL, {
          method: "POST",

          // IMPORTANT:
          // Refresh token is stored in an HttpOnly cookie.
          credentials: "include",

          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Refresh token expired or invalid");
        }

        const data = await response.json();

        if (!data?.token) {
          throw new Error("No access token returned");
        }

        // Save the new 30-minute access token.
        localStorage.setItem("userToken", data.token);
        setToken(data.token);

        return data.token;
      } catch (error) {
        console.error("Token refresh failed:", error);

        // The 90-day refresh session is no longer valid.
        localStorage.removeItem("userToken");
        localStorage.removeItem("userInfo");

        setToken(null);
        setUser(null);

        return null;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    return refreshPromiseRef.current;
  }, []);

  // ---------------------------------------------------------
  // AUTHENTICATED FETCH
  // ---------------------------------------------------------

  const authFetch = useCallback(
    async (url, options = {}) => {
      let currentToken =
        localStorage.getItem("userToken");

      const makeRequest = async (accessToken) => {
        const headers = new Headers(
          options.headers || {}
        );

        if (accessToken) {
          headers.set(
            "Authorization",
            `Bearer ${accessToken}`
          );
        }

        /*
         * credentials: include is important because the
         * refresh token lives in an HttpOnly cookie.
         */
        return fetch(url, {
          ...options,
          headers,
          credentials: "include",
        });
      };

      // -----------------------------------------------------
      // FIRST REQUEST
      // -----------------------------------------------------

      let response = await makeRequest(currentToken);

      // -----------------------------------------------------
      // TOKEN EXPIRED
      // -----------------------------------------------------

      if (response.status === 401) {
        console.log(
          "Access token expired. Refreshing session..."
        );

        const newToken = await refreshAccessToken();

        if (!newToken) {
          return response;
        }

        currentToken = newToken;

        // ---------------------------------------------------
        // RETRY ORIGINAL REQUEST WITH NEW TOKEN
        // ---------------------------------------------------

        response = await makeRequest(currentToken);
      }

      return response;
    },
    [refreshAccessToken]
  );

  // ---------------------------------------------------------
  // LOGOUT
  // ---------------------------------------------------------

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {},
      });
    } catch (error) {
      console.error(
        "Logout request failed:",
        error
      );
    }

    localStorage.removeItem("userToken");
    localStorage.removeItem("userInfo");

    setUser(null);
    setToken(null);
  }, [token]);

  // ---------------------------------------------------------
  // RESTORE SESSION ON APP START
  // ---------------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      try {
        const existingToken =
          localStorage.getItem("userToken");

        /*
         * If we don't have an access token but the browser
         * still has the refresh cookie, try to restore the
         * session automatically.
         */
        if (!existingToken) {
          const newToken =
            await refreshAccessToken();

          if (!newToken) {
            return;
          }

          return;
        }

        /*
         * We have an access token. Don't immediately refresh
         * it on every page load.
         *
         * Protected requests will automatically refresh it
         * when the backend returns 401.
         */
      } catch (error) {
        console.error(
          "Session restoration error:",
          error
        );
      } finally {
        if (!cancelled) {
          setAuthLoading(false);
        }
      }
    };

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, [refreshAccessToken]);

  // ---------------------------------------------------------
  // STORAGE EVENT
  // ---------------------------------------------------------

  useEffect(() => {
    const onStorage = () => {
      try {
        const rawUser =
          localStorage.getItem("userInfo");

        const storedToken =
          localStorage.getItem("userToken");

        setUser(
          rawUser ? JSON.parse(rawUser) : null
        );

        setToken(storedToken || null);
      } catch {
        setUser(null);
        setToken(null);
      }
    };

    window.addEventListener(
      "storage",
      onStorage
    );

    return () => {
      window.removeEventListener(
        "storage",
        onStorage
      );
    };
  }, []);

  // ---------------------------------------------------------
  // CONTEXT
  // ---------------------------------------------------------

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        setAuth,
        logout,

        // New:
        authFetch,
        refreshAccessToken,
        authLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}